// Vanuatu National Syllabus: Geography Senior Secondary Years 11-13
// Based on official syllabus document revised February 2023

export const geographyCurriculum = {
  year11: {
    title: "Year 11 Geography",
    description: "Introduction to geographical concepts and processes",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Natural Processes",
        hours: 30,
        topics: [
          {
            id: "11GEO1.1",
            name: "Earth in Space, Rock Structures, and Landforms",
            description: "Nature of geography and the earth in space with its various landforms and rock structures",
            weeks: "1-3"
          },
          {
            id: "11GEO1.2",
            name: "Geological Processes: Plate Tectonics, Earthquakes and Volcanoes",
            description: "Characteristics of plate tectonics, earthquakes, and volcanoes, and their effects on the landscape",
            weeks: "4-6"
          },
          {
            id: "11GEO1.3",
            name: "Geological Processes: Weathering, Fluvial and Coastal",
            description: "Characteristics of weathering and fluvial and/or coastal processes and their effects on the landscape",
            weeks: "7-9"
          },
          {
            id: "11GEO1.4",
            name: "Atmospheric Processes and Effects",
            description: "World's weather, climate, and their effects on vegetation",
            weeks: "10-12"
          },
          {
            id: "11GEO1.5",
            name: "Natural Resources: Energy and Water",
            description: "Different forms of natural resources, with particular reference to energy and water resources",
            weeks: "13-15"
          }
        ]
      },
      {
        id: "strand2",
        name: "Cultural Processes",
        hours: 44,
        topics: [
          {
            id: "11GEO2.1",
            name: "Population Growth and Movement",
            description: "World population growth, trends, and migration patterns, with particular reference to migration in the Pacific islands",
            weeks: "1-5"
          },
          {
            id: "11GEO2.2",
            name: "Urbanization and Urban Settlements",
            description: "Location, structure and spread of urban settlements, and the problems associated with urban life",
            weeks: "6-10"
          },
          {
            id: "11GEO2.3",
            name: "Tourism: Destinations, Growth, Trends",
            description: "Nature and growth of tourism in the Pacific and worldwide, and the trends involved in the industry",
            weeks: "11-15"
          }
        ]
      },
      {
        id: "strand3",
        name: "Local, Regional and Global Studies",
        hours: 28,
        topics: [
          {
            id: "11GEO3.1",
            name: "Islands and Geology of Vanuatu",
            description: "Geography and geology of Vanuatu",
            weeks: "1-3"
          },
          {
            id: "11GEO3.2",
            name: "Climate of Vanuatu",
            description: "Factors influencing the climate of Vanuatu - its nature, patterns, and links to vegetation",
            weeks: "4-6"
          },
          {
            id: "11GEO3.3",
            name: "Cultural and Political Regions of Vanuatu",
            description: "Administrative regions of Vanuatu, and the need for regional or political boundaries",
            weeks: "7-9"
          },
          {
            id: "11GEO3.4",
            name: "Economy of Vanuatu: Natural Resources and Land Use",
            description: "Use of natural resources in Vanuatu, the various land uses, and the alternatives for the proper management of these resources",
            weeks: "10-12"
          },
          {
            id: "11GEO3.5",
            name: "Biodiversity, Ecosystems and Conservation",
            description: "Fragility and vulnerability of Biodiversity and the need to safeguard and protect natural ecosystems in Vanuatu",
            weeks: "13-15"
          }
        ]
      },
      {
        id: "strand4",
        name: "Geographical Skills and Ideas",
        hours: 30,
        topics: [
          {
            id: "11GEO4.1",
            name: "Types of Maps, Map Projections and Basic Conventions",
            description: "Different types of maps, map projections and basic map drawing conventions",
            weeks: "1-4"
          },
          {
            id: "11GEO4.2",
            name: "Grid References, Coordinates and Time Zones",
            description: "Basic grid references, latitudes, longitudes and international time zones in basic map reading",
            weeks: "5-8"
          },
          {
            id: "11GEO4.3",
            name: "Practical Skills in Geography",
            description: "Different types of graphs, geographical models, pictures, diagrams and cartoons, précis maps and sketch maps",
            weeks: "9-12"
          },
          {
            id: "11GEO4.4",
            name: "Research Skills and Essay Writing",
            description: "Varieties of research methods in collecting primary data, and presenting findings in appropriate essay formats",
            weeks: "13-15"
          }
        ]
      }
    ]
  },
  year12: {
    title: "Year 12 Geography",
    description: "Intermediate geographical analysis and regional studies",
    hoursPerYear: 165,
    hoursPerWeek: 5,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Natural Processes",
        hours: 53,
        topics: [
          {
            id: "12GEO1.1",
            name: "Earth's Landforms and Relief",
            description: "Nature and formation of continental and/or oceanic landforms",
            weeks: "1-4"
          },
          {
            id: "12GEO1.2",
            name: "Living with Natural Hazards: Tectonics and Cyclones",
            description: "Plate tectonics, the distribution of earthquakes, volcanoes and tropical cyclones, their impacts on people and the environment and disaster risk reductions",
            weeks: "5-9"
          },
          {
            id: "12GEO1.3",
            name: "Living with Natural Hazards: Tsunamis, Floods and Droughts",
            description: "Analysis of tsunamis, floods, or droughts - their impacts on people and the environment and strategies to minimize negative effects",
            weeks: "10-14"
          },
          {
            id: "12GEO1.4",
            name: "Global Distribution and Utilization of Natural Resources",
            description: "Global distribution and use of natural resources, and strategies for their proper planning and management",
            weeks: "15-18"
          }
        ]
      },
      {
        id: "strand2",
        name: "Cultural Processes",
        hours: 44,
        topics: [
          {
            id: "12GEO2.1",
            name: "Population Growth: Distribution and Consequences",
            description: "Global patterns and distribution of population growth, and the consequences on people and their environment",
            weeks: "1-5"
          },
          {
            id: "12GEO2.2",
            name: "Urbanization: Planning and Management",
            description: "Factors and processes leading to urbanization, and its effects on the lives of Pacific people and their environment",
            weeks: "6-10"
          },
          {
            id: "12GEO2.3",
            name: "Tourism: Effects, Planning and Management",
            description: "Effects of tourism, including planning and management of tourism on Pacific island countries",
            weeks: "11-15"
          }
        ]
      },
      {
        id: "strand3",
        name: "Local, Regional and Global Studies",
        hours: 30,
        topics: [
          {
            id: "12GEO3.1",
            name: "Geomorphology and Geology of the Pacific Islands",
            description: "Diversity in geography and geology of the Pacific, and its diverse island features",
            weeks: "1-3"
          },
          {
            id: "12GEO3.2",
            name: "Climate and Climate Change in the Pacific Region",
            description: "Major impacts of climate change on Pacific islands environment, and ways in which Pacific people can adapt to these changes",
            weeks: "4-6"
          },
          {
            id: "12GEO3.3",
            name: "Pacific Island Territories and Political Issues",
            description: "Cultural regions of the Pacific and the obstacles to their political development",
            weeks: "7-9"
          },
          {
            id: "12GEO3.4",
            name: "Pacific Island Economies and Challenges",
            description: "Major impacts and challenges on the economy of selected Pacific island countries, and the solutions to these challenges",
            weeks: "10-12"
          },
          {
            id: "12GEO3.5",
            name: "SPREP and Conservation Issues in the Pacific",
            description: "Conservation issues in the Pacific, with particular reference to the role and mission of SPREP in Pacific island states",
            weeks: "13-15"
          }
        ]
      },
      {
        id: "strand4",
        name: "Geographical Skills and Ideas",
        hours: 38,
        topics: [
          {
            id: "12GEO4.1",
            name: "Interpretation of Relief and Land Use from Topographic Maps",
            description: "Topographic features on a map, the various forms of indicating relief and land use on topographic maps",
            weeks: "1-4"
          },
          {
            id: "12GEO4.2",
            name: "Advanced Mapping Skills and Map Interpretation",
            description: "Advanced geographical skills of interpreting large-scale topographic maps, geological maps and aerial photos",
            weeks: "5-9"
          },
          {
            id: "12GEO4.3",
            name: "Practical Skills and Key Geographic Ideas",
            description: "Importance of various maps, graphs, statistical data, texts, photographs and cartoons to present information",
            weeks: "10-13"
          },
          {
            id: "12GEO4.4",
            name: "Communication and Research Skills",
            description: "Skills required to effectively carry out research and communicate information and ideas",
            weeks: "14-18"
          }
        ]
      }
    ]
  },
  year13: {
    title: "Year 13 Geography",
    description: "Advanced geographical analysis and global perspectives",
    hoursPerYear: 165,
    hoursPerWeek: 5,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      internalBreakdown: {
        task1: 15,
        task2: 10,
        task3: 15
      }
    },
    strands: [
      {
        id: "strand1",
        name: "Natural Processes",
        hours: 42,
        topics: [
          {
            id: "13GEO1.1",
            name: "Earth's Internal and External Forces",
            description: "Earth's internal and external forces, and the various landforms they create",
            weeks: "1-5"
          },
          {
            id: "13GEO1.2",
            name: "Tectonic, Volcanic, Fluvial or Coastal Processes",
            description: "Tectonic, volcanic, fluvial or coastal processes - their elements, interactions and spatial variations within a given geographic setting",
            weeks: "6-10"
          },
          {
            id: "13GEO1.3",
            name: "Cyclonic and Aeolian Processes",
            description: "Aeolian processes - their elements, interactions and spatial variations within given geographic settings",
            weeks: "11-14"
          }
        ]
      },
      {
        id: "strand2",
        name: "Cultural Processes",
        hours: 33,
        topics: [
          {
            id: "13GEO2.1",
            name: "Population Movement: Migration",
            description: "Population movement: migration - its elements, interactions, spatial variations and impacts within the geographic setting",
            weeks: "1-4"
          },
          {
            id: "13GEO2.2",
            name: "Industrialization or Agricultural Changes",
            description: "Industrial or agricultural changes - its elements, interactions, spatial variations and impacts within the geographic setting",
            weeks: "5-8"
          },
          {
            id: "13GEO2.3",
            name: "Tourism",
            description: "Tourism - its elements, interactions, spatial variations and impacts within the geographic setting",
            weeks: "9-12"
          }
        ]
      },
      {
        id: "strand3",
        name: "Local, Regional and Global Studies",
        hours: 33,
        topics: [
          {
            id: "13GEO3.1",
            name: "Advanced Geomorphology",
            description: "Advanced understanding of landforms and geological processes at local, regional and global scales",
            weeks: "1-3"
          },
          {
            id: "13GEO3.2",
            name: "Issues Relating to Climate Change",
            description: "Characteristics of climate change - its patterns, and effects on people and the environment",
            weeks: "4-6"
          },
          {
            id: "13GEO3.3",
            name: "Global, Cultural and Political Issues",
            description: "Characteristics of global regional and local cultural and political issues - its patterns, links, and significance for people and the environment",
            weeks: "7-9"
          },
          {
            id: "13GEO3.4",
            name: "Issues Relating to the World Economy",
            description: "Characteristics of issues relating to the local, regional and world economy, its patterns and impacts on people and the environment",
            weeks: "10-12"
          },
          {
            id: "13GEO3.5",
            name: "Treaties Relating to Biodiversity and Conservation",
            description: "Characteristics of treaties relating to biodiversity (CITES, UNEP) - their patterns, links, and significance to people and the environment",
            weeks: "13-15"
          }
        ]
      },
      {
        id: "strand4",
        name: "Geographical Skills and Ideas",
        hours: 52,
        topics: [
          {
            id: "13GEO4.1",
            name: "Skills in Reading and Interpreting Geographical Resources",
            description: "Practical skills in geography such as reading and sketching maps, interpreting photographs, aerial photographs, continuums, cartoons and statistical data",
            weeks: "1-6"
          },
          {
            id: "13GEO4.2",
            name: "Geographic Ideas in Planning and Decision-making",
            description: "Principles of planning and the geographic ideas in understanding planning issues",
            weeks: "7-12"
          },
          {
            id: "13GEO4.3",
            name: "Appropriate Research Procedures",
            description: "Application of the appropriate research skills and procedures in selecting, collecting, organizing, presenting and analyzing information",
            weeks: "13-18"
          }
        ]
      }
    ]
  }
};

export const geographySchemeOfWork = {
  year11: {
    term1: [
      { week: 1, strand: "Natural Processes", topic: "Earth in Space - Introduction to Geography", activities: "Lecture, Map study, Discussion" },
      { week: 2, strand: "Natural Processes", topic: "Rock Structures and Types", activities: "Rock samples examination, Classification exercise" },
      { week: 3, strand: "Natural Processes", topic: "Landforms - Formation and Types", activities: "Diagram drawing, Case studies" },
      { week: 4, strand: "Natural Processes", topic: "Plate Tectonics Theory", activities: "Video presentation, Model making" },
      { week: 5, strand: "Natural Processes", topic: "Earthquakes - Causes and Effects", activities: "Seismograph study, News analysis" },
      { week: 6, strand: "Natural Processes", topic: "Volcanoes - Types and Distribution", activities: "Map work, Case study - Vanuatu volcanoes" },
      { week: 7, strand: "Natural Processes", topic: "Weathering Processes", activities: "Field observation, Photo analysis" },
      { week: 8, strand: "Natural Processes", topic: "Fluvial Processes - Rivers", activities: "River basin study, Diagram interpretation" },
      { week: 9, strand: "Natural Processes", topic: "Coastal Processes", activities: "Coastal fieldwork preparation, Photo study" },
      { week: 10, strand: "Natural Processes", topic: "Weather and Climate", activities: "Weather data recording, Climate graphs" }
    ],
    term2: [
      { week: 1, strand: "Cultural Processes", topic: "World Population Growth", activities: "Statistical analysis, Graph construction" },
      { week: 2, strand: "Cultural Processes", topic: "Population Trends", activities: "Data interpretation, Comparison charts" },
      { week: 3, strand: "Cultural Processes", topic: "Migration Patterns", activities: "Case studies, Map work" },
      { week: 4, strand: "Cultural Processes", topic: "Pacific Migration", activities: "Research project, Interview skills" },
      { week: 5, strand: "Cultural Processes", topic: "Urban Settlement Patterns", activities: "Urban mapping, Photo analysis" },
      { week: 6, strand: "Cultural Processes", topic: "Urban Problems", activities: "Case study analysis, Group discussion" },
      { week: 7, strand: "Cultural Processes", topic: "Tourism Growth", activities: "Statistics interpretation, Trend analysis" },
      { week: 8, strand: "Cultural Processes", topic: "Tourism Impacts", activities: "Debate, Essay writing" },
      { week: 9, strand: "Local Studies", topic: "Vanuatu Geography Overview", activities: "Map study, Island profiles" },
      { week: 10, strand: "Local Studies", topic: "Geology of Vanuatu", activities: "Rock sample study, Field trip preparation" }
    ],
    term3: [
      { week: 1, strand: "Local Studies", topic: "Climate of Vanuatu", activities: "Climate data analysis, Weather mapping" },
      { week: 2, strand: "Local Studies", topic: "Vegetation and Climate Links", activities: "Vegetation mapping, Photo interpretation" },
      { week: 3, strand: "Local Studies", topic: "Political Regions of Vanuatu", activities: "Administrative mapping, Boundary study" },
      { week: 4, strand: "Local Studies", topic: "Natural Resources of Vanuatu", activities: "Resource mapping, Management strategies" },
      { week: 5, strand: "Local Studies", topic: "Biodiversity and Conservation", activities: "Conservation case studies, Project work" },
      { week: 6, strand: "Skills", topic: "Map Types and Projections", activities: "Map comparison, Projection exercises" },
      { week: 7, strand: "Skills", topic: "Grid References and Coordinates", activities: "Map reading exercises, Coordinate plotting" },
      { week: 8, strand: "Skills", topic: "Graph Construction", activities: "Data to graph conversion, Interpretation" },
      { week: 9, strand: "Skills", topic: "Sketch Maps and Diagrams", activities: "Practical drawing, Annotation practice" },
      { week: 10, strand: "Skills", topic: "Research and Essay Writing", activities: "Research methodology, Essay structure" }
    ]
  },
  year12: {
    term1: [
      { week: 1, strand: "Natural Processes", topic: "Continental Landforms", activities: "Topographic map study, Diagram analysis" },
      { week: 2, strand: "Natural Processes", topic: "Oceanic Landforms", activities: "Ocean floor mapping, Model study" },
      { week: 3, strand: "Natural Processes", topic: "Plate Tectonics Review", activities: "Advanced plate boundary study" },
      { week: 4, strand: "Natural Processes", topic: "Earthquake Hazards", activities: "Risk assessment, Preparedness planning" },
      { week: 5, strand: "Natural Processes", topic: "Volcanic Hazards", activities: "Hazard mapping, Case study analysis" },
      { week: 6, strand: "Natural Processes", topic: "Tropical Cyclones", activities: "Cyclone tracking, Impact assessment" },
      { week: 7, strand: "Natural Processes", topic: "Disaster Risk Reduction", activities: "DRR planning, Community strategies" },
      { week: 8, strand: "Natural Processes", topic: "Tsunamis", activities: "Tsunami warning systems, Case studies" },
      { week: 9, strand: "Natural Processes", topic: "Floods and Droughts", activities: "Flood mapping, Drought mitigation" },
      { week: 10, strand: "Natural Processes", topic: "Natural Resource Distribution", activities: "Global resource mapping" }
    ],
    term2: [
      { week: 1, strand: "Cultural Processes", topic: "Global Population Distribution", activities: "Population density mapping" },
      { week: 2, strand: "Cultural Processes", topic: "Population Consequences", activities: "Impact analysis, Policy study" },
      { week: 3, strand: "Cultural Processes", topic: "Urbanization Factors", activities: "Push-pull factors analysis" },
      { week: 4, strand: "Cultural Processes", topic: "Urban Planning", activities: "Urban planning principles, Case studies" },
      { week: 5, strand: "Cultural Processes", topic: "Pacific Urbanization", activities: "Pacific city studies, Comparison work" },
      { week: 6, strand: "Cultural Processes", topic: "Tourism Effects", activities: "Impact assessment matrix" },
      { week: 7, strand: "Cultural Processes", topic: "Tourism Planning", activities: "Sustainable tourism models" },
      { week: 8, strand: "Regional Studies", topic: "Pacific Geomorphology", activities: "Island type classification" },
      { week: 9, strand: "Regional Studies", topic: "Pacific Climate Change", activities: "Climate data analysis" },
      { week: 10, strand: "Regional Studies", topic: "Pacific Adaptation Strategies", activities: "Adaptation planning" }
    ],
    term3: [
      { week: 1, strand: "Regional Studies", topic: "Pacific Political Issues", activities: "Political mapping, Issue analysis" },
      { week: 2, strand: "Regional Studies", topic: "Pacific Economies", activities: "Economic data interpretation" },
      { week: 3, strand: "Regional Studies", topic: "SPREP and Conservation", activities: "SPREP role study, Conservation projects" },
      { week: 4, strand: "Skills", topic: "Topographic Map Interpretation", activities: "Relief and land use mapping" },
      { week: 5, strand: "Skills", topic: "Geological Map Reading", activities: "Geological map exercises" },
      { week: 6, strand: "Skills", topic: "Aerial Photo Interpretation", activities: "Photo analysis techniques" },
      { week: 7, strand: "Skills", topic: "Graph and Data Analysis", activities: "Statistical interpretation" },
      { week: 8, strand: "Skills", topic: "Research Methods", activities: "Research project work" },
      { week: 9, strand: "Skills", topic: "Communication Skills", activities: "Presentation preparation" },
      { week: 10, strand: "Skills", topic: "Research Project Completion", activities: "Project submission, Review" }
    ]
  },
  year13: {
    term1: [
      { week: 1, strand: "Natural Processes", topic: "Internal Forces Review", activities: "Advanced plate tectonics" },
      { week: 2, strand: "Natural Processes", topic: "External Forces", activities: "Weathering and erosion processes" },
      { week: 3, strand: "Natural Processes", topic: "Landform Creation", activities: "Landform case studies" },
      { week: 4, strand: "Natural Processes", topic: "Tectonic Processes Advanced", activities: "Complex tectonic analysis" },
      { week: 5, strand: "Natural Processes", topic: "Volcanic Processes Advanced", activities: "Volcanic landform study" },
      { week: 6, strand: "Natural Processes", topic: "Fluvial Processes Advanced", activities: "River system analysis" },
      { week: 7, strand: "Natural Processes", topic: "Coastal Processes Advanced", activities: "Coastal landform study" },
      { week: 8, strand: "Natural Processes", topic: "Cyclonic Processes", activities: "Cyclone dynamics study" },
      { week: 9, strand: "Natural Processes", topic: "Aeolian Processes", activities: "Wind erosion and deposition" },
      { week: 10, strand: "Natural Processes", topic: "Process Integration", activities: "Systems analysis" }
    ],
    term2: [
      { week: 1, strand: "Cultural Processes", topic: "Migration Analysis", activities: "Migration pattern study" },
      { week: 2, strand: "Cultural Processes", topic: "Migration Impacts", activities: "Impact assessment" },
      { week: 3, strand: "Cultural Processes", topic: "Industrialization", activities: "Industrial geography" },
      { week: 4, strand: "Cultural Processes", topic: "Agricultural Changes", activities: "Agricultural system analysis" },
      { week: 5, strand: "Cultural Processes", topic: "Tourism Advanced", activities: "Tourism system analysis" },
      { week: 6, strand: "Global Studies", topic: "Climate Change Issues", activities: "Climate change analysis" },
      { week: 7, strand: "Global Studies", topic: "Global Political Issues", activities: "Geopolitics study" },
      { week: 8, strand: "Global Studies", topic: "World Economy Issues", activities: "Economic geography" },
      { week: 9, strand: "Global Studies", topic: "Biodiversity Treaties", activities: "CITES and UNEP study" },
      { week: 10, strand: "Global Studies", topic: "Conservation Integration", activities: "Global conservation analysis" }
    ],
    term3: [
      { week: 1, strand: "Skills", topic: "Advanced Map Reading", activities: "Complex map interpretation" },
      { week: 2, strand: "Skills", topic: "Photograph Interpretation", activities: "Advanced photo analysis" },
      { week: 3, strand: "Skills", topic: "Statistical Analysis", activities: "Advanced data interpretation" },
      { week: 4, strand: "Skills", topic: "Planning Principles", activities: "Geographic planning" },
      { week: 5, strand: "Skills", topic: "Decision Making", activities: "Decision analysis techniques" },
      { week: 6, strand: "Skills", topic: "Research Design", activities: "Research methodology" },
      { week: 7, strand: "Skills", topic: "Data Collection", activities: "Fieldwork techniques" },
      { week: 8, strand: "Skills", topic: "Data Analysis", activities: "Analysis techniques" },
      { week: 9, strand: "Skills", topic: "Presentation Skills", activities: "Report writing" },
      { week: 10, strand: "Review", topic: "Exam Preparation", activities: "Revision and practice" }
    ]
  }
};

export default geographyCurriculum;
